import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { registerQuery } from '@adornis/baseql/metadata/register.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';

// ---------------------------------------------------------------------------
// Apollo single-lead detail view.
//
// Shared baseQL operation so the wizard lead preview can fetch full Apollo
// enrichment for a single apolloPersonId and show a detail modal. The actual
// enrichment call is injected via bindApolloLeadDetailResolvers(...) so this
// file stays free of server-only imports.
// ---------------------------------------------------------------------------

@Entity()
export class ApolloLeadDetailView extends AdornisEntity {
  static override _class = 'ApolloLeadDetailView';

  @Field(type => String) firstName!: string;
  @Field(type => String) lastName!: string;
  @Field(type => String) email!: string;
  @Field(type => String) emailStatus?: string;
  @Field(type => String) phone?: string;
  @Field(type => String) linkedinUrl?: string;
  @Field(type => String) jobTitle?: string;
  @Field(type => String) company?: string;
  @Field(type => String) industry?: string;
  @Field(type => String) city?: string;
  @Field(type => String) state?: string;
  @Field(type => String) country?: string;
}

/**
 * One CSV-export row after Apollo Bulk People Enrichment — real name/email/
 * LinkedIn instead of the obfuscated search preview. Fields are optional:
 * Apollo can fail to match a given person id, in which case the export falls
 * back to the unenriched preview data for that row.
 */
@Entity()
export class ApolloBulkEnrichedLead extends AdornisEntity {
  static override _class = 'ApolloBulkEnrichedLead';

  static override resolveID(entity: { apolloPersonId?: string }): string {
    if (!entity.apolloPersonId) throw new Error('ApolloBulkEnrichedLead is missing apolloPersonId');
    return entity.apolloPersonId;
  }

  @Field(type => String) apolloPersonId!: string;
  @Field(type => String) firstName?: string;
  @Field(type => String) lastName?: string;
  @Field(type => String) email?: string;
  @Field(type => String) linkedinUrl?: string;
  @Field(type => String) jobTitle?: string;
  @Field(type => String) company?: string;
  @Field(type => String) industry?: string;
}

export interface ApolloLeadDetailResolverBindings {
  getApolloLeadDetail: (apolloPersonId: string) => Promise<ApolloLeadDetailView>;
  enrichApolloLeadsForExport: (apolloPersonIds: string[]) => Promise<ApolloBulkEnrichedLead[]>;
}

const notBound = (name: string) => () => {
  throw new Error(`Apollo lead detail resolver "${name}" not bound — server forgot to call bindApolloLeadDetailResolvers().`);
};

let bindings: ApolloLeadDetailResolverBindings = {
  getApolloLeadDetail: notBound('getApolloLeadDetail') as any,
  enrichApolloLeadsForExport: notBound('enrichApolloLeadsForExport') as any,
};

export const bindApolloLeadDetailResolvers = (impls: ApolloLeadDetailResolverBindings): void => {
  bindings = impls;
};

export const getApolloLeadDetail = registerQuery({
  operationName: 'getApolloLeadDetail',
  type: () => ApolloLeadDetailView,
  params: [{ name: 'apolloPersonId', type: () => String }],
  resolve: (apolloPersonId: string) => async (_gqlFields: BaseQLSelectionSet<ApolloLeadDetailView>) =>
    bindings.getApolloLeadDetail(apolloPersonId),
});

/**
 * Enriches a batch of Apollo person ids (from a lead search) for CSV export —
 * costs Apollo credits per matched person, so only call this when the user
 * explicitly opts into an enriched export, not on every preview refresh.
 */
export const enrichApolloLeadsForExport = registerQuery({
  operationName: 'enrichApolloLeadsForExport',
  type: () => [ApolloBulkEnrichedLead],
  params: [{ name: 'apolloPersonIds', type: () => [String] }],
  resolve: (apolloPersonIds: string[]) => async (_gqlFields: BaseQLSelectionSet<ApolloBulkEnrichedLead>) =>
    bindings.enrichApolloLeadsForExport(apolloPersonIds),
});
